import { Component, ViewChild, AfterViewInit, ChangeDetectorRef, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { WizardPageComponent } from '../../../../core/wizard/wizard-page-component';
// import { CaptureDescriptionWizard } from '../capture-description-wizard';
import { MapSearchPdfPageComponent } from '../../map-search-pdf-page/map-search-pdf-page.component';

import { ToastService } from 'src/app/core/toast/toast.service';
import { ShapeService } from 'src/app/core/depot/shape.service';
import { SpatialShapeType } from 'src/app/core/depot/spatial-shape-type.enum';

// Import font files
import '../../../../../assets/fonts/SofiaSans-Bold-normal.js';
import "../../../../../assets/fonts/SofiaSans-Regular-normal.js";
//import { BannerService } from 'src/app/core/banner/banner.service';

@Component({
  standalone: false,
  selector: 'mtn-capture-description-page',
  templateUrl: './capture-description-page.component.html',
  styleUrls: ['./capture-description-page.component.scss']
})
export class CaptureDescriptionPageComponent extends WizardPageComponent<any> implements AfterViewInit {
  key = 'capture-description';

  @ViewChild('mapSearchPdf') mapSearchPdf: MapSearchPdfPageComponent;
  @ViewChildren('pdfPage') pdfPages!: QueryList<ElementRef>;


  form = new UntypedFormGroup({
    description: new UntypedFormControl('', [Validators.required, this.customMaxLengthValidator(), this.noWhitespaceValidator, this.maxLinesValidator(4)])
  });

  isExporting = false;


getCustomCharacterCount(): number {
  const value = this.form.controls.description.value || '';
  const manualLines = value.split('\n');
  let totalCharacterCount = 0;
  
  manualLines.forEach((line, index) => {
    if (index === 0) {
      totalCharacterCount += line.length;
    } else {
      totalCharacterCount += 83 + line.length;
    }
  });
  
  return totalCharacterCount;
}

customMaxLengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const manualLines = control.value.split('\n');
    let totalCharacterCount = 0;
    
    manualLines.forEach((line, index) => {
      if (index === 0) {
        totalCharacterCount += line.length;
      } else {
        totalCharacterCount += 83 + line.length; // 83 for line break + actual characters
      }
    });

    return totalCharacterCount > 150
      ? { maxlength: { requiredLength: 150, actualLength: totalCharacterCount } }
      : null;
  };
}

maxLinesValidator(maxLines: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const manualLines = control.value.split('\n');
    let totalLines = 0;
    
    manualLines.forEach((line, index) => {
      const charsPerLine = index === 0 ? 80 : 83;
      totalLines += Math.ceil(line.length / charsPerLine) || 1;
    });

    return totalLines > maxLines
      ? { maxLines: { requiredLines: maxLines, actualLines: totalLines } }
      : null;
  };
}

onKeyDown(event: KeyboardEvent) {
  const value = this.form.controls.description.value || '';
  const manualLines = value.split('\n');
  
  // Count total lines (character wrapping + manual breaks)
  let totalLines = 0;
  manualLines.forEach((line, index) => {
    const charsPerLine = index === 0 ? 80 : 83;
    totalLines += Math.ceil(line.length / charsPerLine) || 1;
  });
  
  // Allow max 2 manual line breaks (Enter key presses)
  const manualBreaks = manualLines.length - 1;
  
  if (event.key === 'Enter' && (manualBreaks >= 2 || totalLines >= 4)) {
    event.preventDefault();
    return;
  }
}

onInput(event: any) {
  const value = event.target.value || '';
  const manualLines = value.split('\n');
  
  // Count total lines (character wrapping + manual breaks)
  let totalLines = 0;
  manualLines.forEach((line, index) => {
    const charsPerLine = index === 0 ? 80 : 83;
    totalLines += Math.ceil(line.length / charsPerLine) || 1;
  });
  
  if (totalLines > 4 || manualLines.length > 3) {
    // Truncate to fit within limits
    let truncatedValue = '';
    let currentTotalLines = 0;
    
    for (let i = 0; i < Math.min(manualLines.length, 3); i++) {
      const line = manualLines[i];
      const charsPerLine = i === 0 ? 80 : 83;
      const lineWraps = Math.ceil(line.length / charsPerLine) || 1;
      
      if (currentTotalLines + lineWraps <= 4) {
        if (i > 0) truncatedValue += '\n';
        truncatedValue += line;
        currentTotalLines += lineWraps;
      } else {
        // Truncate this line to fit remaining space
        const remainingLines = 4 - currentTotalLines;
        const maxChars = remainingLines * charsPerLine;
        if (i > 0) truncatedValue += '\n';
        truncatedValue += line.substring(0, maxChars);
        break;
      }
    }
    
    this.form.controls.description.setValue(truncatedValue);
    event.target.value = truncatedValue;
    setTimeout(() => {
      event.target.setSelectionRange(truncatedValue.length, truncatedValue.length);
    });
  }
  
  this.form.controls.description.updateValueAndValidity();
}


  ngAfterViewInit(): void {
    // Listen for form changes
    this.form.get('description').valueChanges.subscribe(() => {
      this.updateDescription();
      this.updateButtonState();
    });
    this.updateDescription();
    this.updateButtonState();
    this.passDataToPdfComponent();
  }

  onLoad(): Observable<any> {
    this.title = 'Snapshot description';
    this.nextButtonText = 'Export';
    this.closeButtonText = 'Cancel';

    if (this.wizard.model.description) {
      this.form.get('description').setValue(this.wizard.model.description);
    }

    this.updateButtonState();
    return of(null);
  }

  updateDescription(): void {
    const description = this.form.get('description')?.value || '';
    this.analysisDescription = description;
    if (this.mapSearchPdf && this.wizard?.model) {
      this.wizard.model.userDescription = description;
      this.mapSearchPdf.analysisDescription = this.analysisDescription;
    }
  }

  updateButtonState(): void {
    this.isActionButtonEnabled = this.form.valid;
  }

  noWhitespaceValidator(control: any) {
    const value = control.value;
    if (!value || value.toString().trim().length === 0) {
      return { whitespace: true };
    }
    return null;
  }

  passDataToPdfComponent(): void {
    if (this.mapSearchPdf && this.wizard?.model) {
      this.mapSearchPdf.wizard = { model: this.wizard.model };
      this.mapSearchPdf.ngOnInit();
    }
  }

  onNext(): Observable<string> {
    // Disable button to prevent multiple clicks
    this.isActionButtonEnabled = false;
    this.isExporting = true;

    this.wizard.model.result = this.form.get('description').value;
    this.wizard.model.userDescription = this.form.get('description').value;
    this.updateDescription();

    return new Observable<string>(observer => {
      // Gather PDF data when export button is clicked
      if (this.wizard.model.getPdfData) {
        this.wizard.model.getPdfData().then((pdfData: any) => {
          // Merge the gathered data into wizard model
          Object.assign(this.wizard.model, pdfData);
          
          console.log('Exporting PDF with store count:', pdfData.totalStoreCount, 'Store list length:', pdfData.storeList.length);
          
          // Initialize filtered stores with the new data
          this.initializeFilteredStores();
          this.initializeStoreLegend();
          
          const mapCenter = pdfData.mapCenter;
          if (mapCenter && mapCenter.lat && mapCenter.lng) {
            this.shapeService.searchIntersectingShape({
              latitude: mapCenter.lat,
              longitude: mapCenter.lng,
              type: SpatialShapeType.CBSA
            }).pipe(
              switchMap((response: any) => {
                const cbsaName = response?.features?.[0]?.properties?.name;
                this.wizard.model.cbsaName = cbsaName || 'Current Region';
                this.initializeActiveFilters();
                this.cdr.detectChanges();
                return this.exportToPdfAndClose();
              }),
              catchError((error) => {
                console.error('Error fetching CBSA:', error);
                this.wizard.model.cbsaName = 'Current Region';
                this.initializeActiveFilters();
                this.cdr.detectChanges();
                return this.exportToPdfAndClose();
              })
            ).subscribe({
              next: (result) => {
                observer.next(result);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
              }
            });
          } else {
            this.wizard.model.cbsaName = 'Current Region';
            this.initializeActiveFilters();
            this.cdr.detectChanges();
            this.exportToPdfAndClose().subscribe({
              next: (result) => {
                observer.next(result);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
              }
            });
          }
        }).catch((error) => {
          console.error('Error gathering PDF data:', error);
          this.isActionButtonEnabled = true;
          this.isExporting = false;
          observer.error(error);
        });
      } else {
        // Fallback to existing logic if no getPdfData function
        const mapCenter = this.wizard.model.mapCenter;
        if (mapCenter && mapCenter.lat && mapCenter.lng) {
          this.shapeService.searchIntersectingShape({
            latitude: mapCenter.lat,
            longitude: mapCenter.lng,
            type: SpatialShapeType.CBSA
          }).pipe(
            switchMap((response: any) => {
              const cbsaName = response?.features?.[0]?.properties?.name;
              this.wizard.model.cbsaName = cbsaName || 'Current Region';
              this.initializeActiveFilters();
              this.cdr.detectChanges();
              return this.exportToPdfAndClose();
            }),
            catchError((error) => {
              console.error('Error fetching CBSA:', error);
              this.wizard.model.cbsaName = 'Current Region';
              this.initializeActiveFilters();
              this.cdr.detectChanges();
              return this.exportToPdfAndClose();
            })
          ).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            }
          });
        } else {
          this.wizard.model.cbsaName = 'Current Region';
          this.initializeActiveFilters();
          this.cdr.detectChanges();
          this.exportToPdfAndClose().subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            }
          });
        }
      }
    });
  }


    exportDate = 'October  14,2025';
    exportTime = '2:34 PM EST';
    analysisDescription = '';
    capturedMapImage: string = '';
    //exportPdfButtonText = 'Export PDF';
    isDataLoading = true;

    storeLegendItems: string[] = [];
    storeLegendItemsSecondary: string[] = [];

    filterRegion = '';
    filterFits = '';
    filterStatus = '';
    showActiveFilters = false;

    areaSquareFeet = '2,847,392 sq ft';
    areaAcres = '65.3 acres';
    perimeterFeet = '8,420 ft';
    perimeterMiles = '1.59 miles';
    centerPointLat = '41.88430N';
    centerPointLong = '87.63240W';

    filteredStores: any[] = [];
    totalStoreCount = 0;
    showPolygonAnalysis = false;
    polygonDrawnTime: number = 0;
    polygonTimeout = 100; // 15 seconds

    // filteredStores = [
    //   { storeName: 'Jewel-Osco #3042', banner: 'Jewel-Osco', format: 'Conventional', address: '1224 S Wabash Ave' },
    //   { storeName: 'Mariano\'s Fresh Market', banner: 'Mariano\'s', format: 'Conventional', address: '333 E Benton Pl' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Aldi Store #94', banner: 'Aldi', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Jewel-Osco #3042', banner: 'Jewel-Osco', format: 'Conventional', address: '1224 S Wabash Ave' },
    //   { storeName: 'Mariano\'s Fresh Market', banner: 'Mariano\'s', format: 'Conventional', address: '333 E Benton Pl' },
    //   { storeName: 'Jewel-Osco #3042', banner: 'Jewel-Osco', format: 'Conventional', address: '1224 S Wabash Ave' },
    //   { storeName: 'Mariano\'s Fresh Market', banner: 'Mariano\'s', format: 'Conventional', address: '333 E Benton Pl' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Aldi Store #94', banner: 'Aldi', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Jewel-Osco #3042', banner: 'Jewel-Osco', format: 'Conventional', address: '1224 S Wabash Ave' },
    //   { storeName: 'Mariano\'s Fresh Market', banner: 'Mariano\'s', format: 'Conventional', address: '333 E Benton Pl' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Mariano\'s Fresh Market', banner: 'Mariano\'s', format: 'Conventional', address: '333 E Benton Pl' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Aldi Store #94', banner: 'Aldi', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Aldi Store #94', banner: 'Aldi', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Whole Food Market', banner: 'Whole Foods', format: 'Natural Foods', address: '30 W Huron St' },
    //   { storeName: 'Aldi Store #94', banner: 'Aldi', format: 'Natural Foods', address: '30 W Huron St' }
    // ];
    // totalStoreCount = 23;

    constructor(
      private toaster: ToastService,
      private shapeService: ShapeService,
      private cdr: ChangeDetectorRef
    ) {
      super();
    }

    ngOnInit(): void {
      this.initializeExportDate();
      this.initializeUserDescription();
      this.captureAllAvailableStores();
      this.initializeStoreLegend();
      this.getMapDataFromGlobalState();
      this.initializeMapCenterCoordinates();

      // Initialize CBSA name if not already set
      if (!this.wizard?.model?.cbsaName) {
        this.fetchCbsaName();
      }

      // Get global polygon timer
      this.polygonDrawnTime = (window as any).polygonDrawnTime || 0;
      this.showPolygonAnalysis = false;
      this.checkPolygonAnalysis();

      // Set loading to false after data initialization
      setTimeout(() => {
        this.isDataLoading = false;
      }, 1000);
    }

    captureAllAvailableStores(): void {
      // Capture stores from various global sources
      const mapComponent = (window as any).mapComponent;
      const storeService = (window as any).storeService;
      const currentMapStores = (window as any).currentMapStores;

      if (mapComponent && mapComponent.stores) {
        (window as any).mapStores = mapComponent.stores;
      }

      if (storeService && storeService.getAllStores) {
        try {
          const allStores = storeService.getAllStores();
          (window as any).currentStores = allStores;
        } catch (error) {
          // Silent error handling
        }
      }

      if (currentMapStores) {
        (window as any).mapStores = [...((window as any).mapStores || []), ...currentMapStores];
      }
    }

    getMapDataFromGlobalState(): void {
      // Try to get map data from window object or global state
      const globalMapData = (window as any).currentMapData;
      if (globalMapData) {
        this.wizard.model.selectionMode = globalMapData.selectionMode;
        this.wizard.model.polygonArea = globalMapData.polygonArea;
        this.wizard.model.centerPoint = globalMapData.centerPoint;
        this.wizard.model.perimeterFeet = globalMapData.perimeterFeet;
        this.wizard.model.perimeterMiles = globalMapData.perimeterMiles;
      }

      // Get captured map image
      const capturedMap = (window as any).capturedMapImage;
      if (capturedMap) {
        this.capturedMapImage = capturedMap;
      } else {
        // Try to capture map now
        this.captureMapNow();
      }

      // Refresh store data after getting map data
      // this.captureAllAvailableStores();
    }

 async captureCurrentMapState() {
  const HIDDEN_CLASS = 'pdf-capture-hidden';

  try {
    const mapSelectors = ['mtn-map', '.mtn-map', '[class*="map"]', 'google-map', '.map-container'];
    let mapElement = null;

    for (const selector of mapSelectors) {
      mapElement = document.querySelector(selector);
      if (mapElement) break;
    }

    if (mapElement) {
      const html2canvas = (await import('html2canvas')).default;

      const elementsToHide = [
        'mtn-maps-controls-toolbar',
        'mtn-filter-toolbar',
        '[class*="toolbar"]',
        '[class*="control"]',
        '.gm-control-active',
        '.gm-bundled-control',
        '.gmnoprint',
        '[class*="maps-control"]',
        '[class*="map-control"]',
        '[class*="drop-pin"]',
        '[class*="pin"]',

        '[class*="tooltip"]',
        '[class*="popup"]',
        '[class*="overlay"]',
        '.map-tooltip',
        '.map-popup',
        '.info-window',
        '.gm-style-iw'
      ];

      const hiddenElements = document.querySelectorAll(elementsToHide.join(', '));
      hiddenElements.forEach(element => element.classList.add(HIDDEN_CLASS));

      try {
        const canvas = await html2canvas(mapElement as HTMLElement, {
          useCORS: true,
          allowTaint: true,
          scale: 2.0,
          logging: false,
          ignoreElements: (element) => {
            return element.classList.contains(HIDDEN_CLASS) ||
                   element.classList.contains('gmnoprint') ||
                   element.classList.contains('gm-control-active') ||
                   element.classList.contains('gm-style-iw') ||
                   element.textContent?.includes('Drop pin to project grocery sales') ||
                   element.getAttribute('title')?.includes('Drop pin') ||
                   element.getAttribute('aria-label')?.includes('Drop pin');
          }
        });

        this.capturedMapImage = canvas.toDataURL('image/png', 1.0);

      } finally {
        hiddenElements.forEach(element => element.classList.remove(HIDDEN_CLASS));
      }
    }
  } catch (error) {
    console.error('Map capture failed:', error);
  }
}

    async captureMapNow() {
      // Find and store map reference
      const mapElements = Array.from(document.querySelectorAll('mtn-map, [data-map-id], .gm-style'));
      for (const element of mapElements) {
        if ((element as any).__ngContext__ && Array.isArray((element as any).__ngContext__)) {
          const contexts = (element as any).__ngContext__;
          for (let i = 0; i < contexts.length; i++) {
            if (contexts[i] && contexts[i].map && contexts[i].map.getBounds) {
              (window as any).currentMap = contexts[i].map;
              break;
            }
          }
        }
      }

      await this.captureCurrentMapState();
    }

    checkPolygonAnalysis(): void {
      const hasActivePolygon = this.hasActivePolygonOnMap();
      const hasSelectedStores = this.totalStoreCount > 0;
      const isWithinTimeLimit = this.isPolygonWithinTimeLimit();



      this.showPolygonAnalysis = hasActivePolygon && hasSelectedStores && isWithinTimeLimit;
      this.showPolygonAnalysis = false;
      if (this.showPolygonAnalysis) {
        const globalMapData = (window as any).currentMapData;
        const currentSelectionMode = globalMapData?.selectionMode || this.wizard?.model?.selectionMode;
        const currentPolygonArea = globalMapData?.polygonArea;

        if (currentSelectionMode === 'LASSO' && currentPolygonArea) {
          this.updatePolygonMetrics(currentPolygonArea);
        } else if (currentSelectionMode === 'RING') {
          this.updateRingMetrics();
        }
      }
    }

    hasActivePolygonOnMap(): boolean {
      const globalMapData = (window as any).currentMapData;
      const currentSelectionMode = globalMapData?.selectionMode;
      const currentPolygonArea = globalMapData?.polygonArea;

      if (currentSelectionMode === 'LASSO') {
        const hasArea = currentPolygonArea > 0;
        if (hasArea) {
          // Check if this is a new polygon (different area or no previous timer)
          const lastPolygonArea = (window as any).lastPolygonArea;
          if (!lastPolygonArea || lastPolygonArea !== currentPolygonArea) {
            (window as any).polygonDrawnTime = Date.now();
            (window as any).lastPolygonArea = currentPolygonArea;
          }
          this.polygonDrawnTime = (window as any).polygonDrawnTime;
          return hasArea;
        } else {
          (window as any).polygonDrawnTime = 0;
          (window as any).lastPolygonArea = 0;
          this.polygonDrawnTime = 0;
          return false;
        }
      } else if (currentSelectionMode === 'RING') {
        // For ring, reset timer each time (since ring can be redrawn)
        (window as any).polygonDrawnTime = Date.now();
        this.polygonDrawnTime = (window as any).polygonDrawnTime;
        return true;
      } else {
        (window as any).polygonDrawnTime = 0;
        (window as any).lastPolygonArea = 0;
        this.polygonDrawnTime = 0;
        return false;
      }
    }

    isPolygonWithinTimeLimit(): boolean {
      if (!this.polygonDrawnTime) return false;
      const currentTime = Date.now();
      return (currentTime - this.polygonDrawnTime) <= this.polygonTimeout;
    }

    updatePolygonMetrics(areaInSquareMeters: number): void {
      const areaInSquareFeet = areaInSquareMeters * 10.764;
      const areaInAcres = areaInSquareMeters * 0.000247105;

      this.areaSquareFeet = `${Math.round(areaInSquareFeet).toLocaleString()} sq ft`;
      this.areaAcres = `${areaInAcres.toFixed(1)} acres`;

      // Update center point if available
      const centerPoint = this.wizard?.model?.centerPoint || this.wizard?.model?.mapCenter;
      if (centerPoint) {
        this.centerPointLat = `${centerPoint.lat.toFixed(4)}`;
        this.centerPointLong = `${centerPoint.lng.toFixed(4)}`;
      }
    }

    updateRingMetrics(): void {
      const perimeterFeet = this.wizard?.model?.perimeterFeet;
      const perimeterMiles = this.wizard?.model?.perimeterMiles;
      const centerPoint = this.wizard?.model?.centerPoint || this.wizard?.model?.mapCenter;

      if (perimeterFeet && perimeterMiles) {
        this.perimeterFeet = `${Math.round(perimeterFeet).toLocaleString()} ft`;
        this.perimeterMiles = `${perimeterMiles.toFixed(2)} miles`;
      }

      if (centerPoint) {
        this.centerPointLat = `${centerPoint.lat.toFixed(4)}`;
        this.centerPointLong = `${centerPoint.lng.toFixed(4)}`;
      }
    }

    // onLoad(): Observable<any> {
    //   return of(null);
    // }

    onExportPdf(): Observable<string> {
      this.exportToPdf();
      return of(null);
    }

    initializeExportDate(): void {
      const now = new Date();
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      };
      this.exportDate = now.toLocaleDateString('en-US', options);

      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York',
        timeZoneName: 'short'
      };
      this.exportTime = now.toLocaleTimeString('en-US', timeOptions);
    }

    initializeUserDescription(): void {
      const userDescription = this.wizard?.model?.userDescription || this.form.get('description')?.value;
      this.analysisDescription = userDescription || 'No description provided';
    }

    initializeStoreLegend(): void {
      this.initializeFilteredStores();

      const formatCounts: { [key: string]: number } = {};
      this.filteredStores.forEach(store => {
        const format = store.format;
        if (format && format !== 'N/A') {
          formatCounts[format] = (formatCounts[format] || 0) + 1;
        }
      });

      const sortedItems = Object.entries(formatCounts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count)
        .map(item => `${item.label} (${item.count})`);

      this.storeLegendItems = sortedItems.slice(0, 5);
      this.storeLegendItemsSecondary = sortedItems.slice(5);

      this.initializeActiveFilters();
    }

    initializeActiveFilters(): void {
      const activeFilters = this.wizard?.model?.activeFilters || {};
      const cbsaName = this.wizard?.model?.cbsaName || 'Current Region';

      this.filterRegion = cbsaName;
      this.filterFits = activeFilters.fits || 'N/A';
      this.filterStatus = activeFilters.status || 'N/A';

      // Check if any meaningful filters are applied (excluding geojson and internal flags)
      const hasFilters = Object.keys(activeFilters).some(key => {
        if (['hasFitFilters', 'hasStatusFilters', 'hasAnyFilters', 'geojson', 'geoJson', 'region'].includes(key)) {
          return false;
        }
        const hasValue = activeFilters[key] && activeFilters[key] !== 'N/A' && activeFilters[key] !== '';
        return hasValue;
      });

      // Only show active filters if there are actual meaningful filters (not just CBSA)
      this.showActiveFilters = hasFilters;
    }

    getActiveFilterEntries(): {label: string, value: string}[] {
      const activeFilters = this.wizard?.model?.activeFilters || {};
      const entries = [];

      const filterLabels = {
        fits: 'FITs',
        status: 'Status',
        banner: 'Banner',
        area: 'Sales Area',
        total_area: 'Total Area',
        sales: 'Sales Volume',
        sales_sqft: 'Sales/SqFt',
        collections: 'Collections'
      };

      const filterOrder = ['fits', 'status', 'banner', 'area', 'total_area', 'sales', 'sales_sqft', 'collections'];

      // Add CBSA region first if there are other filters applied
      if (this.filterRegion && this.filterRegion !== 'N/A') {
        entries.push({
          label: 'Region',
          value: this.filterRegion
        });
      }

      // Add meaningful filters in specific order
      filterOrder.forEach(key => {
        if (!filterLabels[key]) return;
        if (activeFilters[key] && activeFilters[key] !== 'N/A' && activeFilters[key] !== '') {
          let displayValue = activeFilters[key];
          let filterLabel = filterLabels[key];

          // Check if (Exclusive) is in the value and move it to label
          if (typeof displayValue === 'string' && displayValue.includes('(Exclusive)')) {
            displayValue = displayValue.replace(' (Exclusive)', '').replace('(Exclusive)', '');
            filterLabel += ' (Exclusive)';
          }

          if (key === 'banner') {
            // Handle banner UUID to name conversion for strings
            if (typeof displayValue === 'string' && (displayValue.includes('-') || displayValue.length > 20)) {
              // Try to get from stores first (faster)
              const bannerNameFromStores = this.getBannerNameFromStores(displayValue);
              if (bannerNameFromStores !== displayValue) {
                displayValue = bannerNameFromStores;
              } 
            }
          }

          // Handle object values for specific filters
          if (typeof displayValue === 'object') {

            if ((key === 'sales' || key === 'sales_sqft') && displayValue.min !== undefined && displayValue.max !== undefined) {
              displayValue = `$${displayValue.min} - $${displayValue.max}`;
            } else if ((key === 'area' || key === 'total_area') && displayValue.min !== undefined && displayValue.max !== undefined) {
              displayValue = `${displayValue.min} - ${displayValue.max} sq ft`;
            } else if (key === 'banner') {
              // Banner names should already be resolved in filter-toolbar, just format the display
              if (Array.isArray(displayValue)) {
                displayValue = displayValue.join(', ');
              } else if (typeof displayValue === 'object' && displayValue.name) {
                displayValue = displayValue.name;
              } else if (typeof displayValue !== 'string') {
                displayValue = 'Banner Filter Applied';
              }
              // If it's still a UUID string, try to get from stores as fallback
              if (typeof displayValue === 'string' && (displayValue.includes('-') || displayValue.length > 20)) {
                const bannerName = this.getBannerNameFromStores(displayValue);
                displayValue = bannerName !== displayValue ? bannerName : displayValue;
              }
            } else if (key === 'collections') {
              // Collections names should already be resolved by filter-toolbar
              if (Array.isArray(displayValue)) {
                displayValue = displayValue.join(', ');
              } else if (typeof displayValue === 'object' && displayValue.name) {
                displayValue = displayValue.name;
              } else if (typeof displayValue !== 'string') {
                displayValue = 'Collections Applied';
              }
            } else if (Array.isArray(displayValue)) {
              displayValue = displayValue.join(', ');
            } else {
              displayValue = JSON.stringify(displayValue).replace(/[{}"]/g, '').replace(/,/g, ', ');
            }
          }

          // Format long values for better wrapping
          let formattedValue = displayValue;
          if (typeof displayValue === 'string' && displayValue.length > 50) {
            // For very long values, add word breaks at commas and spaces
            formattedValue = displayValue.replace(/,\s*/g, ', ').replace(/;\s*/g, '; ');
          }
          
          entries.push({
            label: filterLabel,
            value: formattedValue
          });
        }
      });

      // Add any other filters not in the predefined list, excluding geojson and internal flags
      Object.keys(activeFilters).forEach(key => {
        if (!filterLabels[key] &&
            !filterOrder.includes(key) &&
            !['hasFitFilters', 'hasStatusFilters', 'hasAnyFilters', 'geojson', 'geoJson', 'region'].includes(key) &&
            activeFilters[key] && activeFilters[key] !== 'N/A' && activeFilters[key] !== '') {
          entries.push({
            label: key.charAt(0).toUpperCase() + key.slice(1),
            value: activeFilters[key]
          });
        }
      });



      return entries;
    }



    initializeMapCenterCoordinates(): void {
      const mapCenter = this.wizard?.model?.mapCenter;
      if (mapCenter) {
        this.centerPointLat = `${mapCenter.lat.toFixed(5)}N`;
        this.centerPointLong = `${Math.abs(mapCenter.lng).toFixed(5)}W`;
      }
    }

    fetchCbsaName(): void {
      const mapCenter = this.wizard?.model?.mapCenter;
      if (mapCenter && mapCenter.lat && mapCenter.lng) {
        this.shapeService.searchIntersectingShape({
          latitude: mapCenter.lat,
          longitude: mapCenter.lng,
          type: SpatialShapeType.CBSA
        }).pipe(
          catchError((error) => {
            console.error('Error fetching CBSA during init:', error);
            return of(null);
          })
        ).subscribe((response: any) => {
          if (response?.features?.[0]?.properties?.name) {
            this.wizard.model.cbsaName = response.features[0].properties.name;
            this.initializeActiveFilters(); // Refresh filters with CBSA
          } else {
            this.wizard.model.cbsaName = 'Current Region';
            this.initializeActiveFilters();
          }
        });
      } else {
        this.wizard.model.cbsaName = 'Current Region';
        this.initializeActiveFilters();
      }
    }

    initializeFilteredStores(): void {
      const storeList = this.wizard?.model?.storeList || [];
      //const apiTotalCount = this.wizard?.model?.totalStoreCount;

      let filteredList = storeList;

      this.filteredStores = filteredList.map((store1: any) => {
      const rawFit = store1.store?.fit;
      // if empty/null/undefined → return "N/A" exactly as is
      const formattedFit = rawFit
    ? rawFit
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : 'N/A';

      const storeNumber = store1.store?.number;
      const storeName = store1.store?.name || 'N/A';
      const storeNameWithNumber = storeNumber ? `${storeName} #${storeNumber}` : storeName;

      return {
        storeName: storeNameWithNumber,
        banner: store1.store?.banner?.name || 'N/A',
        format: formattedFit,
        address: this.formatAddress(store1.store?.space?.location)
      };
    });

      this.totalStoreCount = this.filteredStores.length;
      this.checkPolygonAnalysis();
    }

    getApiFilterPayload(): any[] {
      // Get filter payload from map's filterGroup (same as API request)
      const filterGroup = (window as any).currentMap?.options?.filterGroup || this.wizard?.model?.filterGroup;
      return filterGroup?.filters || [];
    }

    applyApiFilters(stores: any[], filters: any[]): any[] {
      let filteredList = stores;

      filters.forEach(filter => {
        switch (filter.type) {
          case 'FIT_CATEGORY':
            if (filter.value && Array.isArray(filter.value)) {
              filteredList = filteredList.filter((store1: any) => {
                const storeFit = (store1.store?.fit || store1.fit)?.toUpperCase();
                return filter.value.includes(storeFit);
              });
            }
            break;

          case 'STATUS':
            if (filter.value && Array.isArray(filter.value)) {
              filteredList = filteredList.filter((store1: any) => {
                const storeStatus = store1.store?.currentStatus?.type?.systemName?.toUpperCase() ||
                                   store1.currentStatus?.type?.systemName?.toUpperCase();
                return filter.value.includes(storeStatus);
              });
            }
            break;

          case 'SALES_VOLUME':
            if (filter.value && filter.value.min !== undefined && filter.value.max !== undefined) {
              filteredList = filteredList.filter((store1: any) => {
                const volumeData = store1.store?.volume || store1.volume;
                let storeSales = 0;

                if (volumeData && volumeData.total) {
                  storeSales = volumeData.total;
                } else {
                  storeSales = store1.store?.salesVolume || store1.salesVolume || 0;
                }

                return storeSales === 0 || (storeSales >= filter.value.min && storeSales <= filter.value.max);
              });
            }
            break;

          case 'SALES_SQFT':
            if (filter.value && filter.value.min !== undefined && filter.value.max !== undefined) {
              filteredList = filteredList.filter((store1: any) => {
                const volumeData = store1.store?.volume || store1.volume;
                let storeSalesSqft = 0;

                if (volumeData) {
                  storeSalesSqft = volumeData.salesSqftDisplayType === 'SALES_AREA' ?
                    volumeData.salesSqftBySalesArea : volumeData.salesSqftByTotalArea;
                } else {
                  storeSalesSqft = store1.store?.salesPerSqft || store1.salesPerSqft || 0;
                }

                return storeSalesSqft === 0 || (storeSalesSqft >= filter.value.min && storeSalesSqft <= filter.value.max);
              });
            }
            break;

          case 'SALES_AREA':
            if (filter.value && filter.value.min !== undefined && filter.value.max !== undefined) {
              filteredList = filteredList.filter((store1: any) => {
                const storeArea = store1.store?.grocerySalesArea || store1.grocerySalesArea || 0;
                return storeArea >= filter.value.min && storeArea <= filter.value.max;
              });
            }
            break;

          case 'TOTAL_AREA':
            if (filter.value && filter.value.min !== undefined && filter.value.max !== undefined) {
              filteredList = filteredList.filter((store1: any) => {
                const storeTotalArea = store1.store?.totalArea || store1.totalArea || 0;
                return storeTotalArea >= filter.value.min && storeTotalArea <= filter.value.max;
              });
            }
            break;

          case 'COLLECTION':
          case 'COLLECTIONS':
            if (filter.value && Array.isArray(filter.value)) {
              // Collections filter - stores should be included if they match any collection
              filteredList = filteredList.filter((store1: any) => {
                const storeCollections = store1.store?.collections || store1.collections || [];
                return filter.value.some(collectionId =>
                  storeCollections.some(storeCollection =>
                    storeCollection.uuid === collectionId || storeCollection.id === collectionId
                  )
                );
              });
            }
            break;
        }
      });

      return filteredList;
    }

    removeDuplicateStores(stores: any[]): any[] {
      const seen = new Set();
      return stores.filter(store => {
        const storeObj = store.store || store;
        const key = storeObj?.uuid || storeObj?.id;
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    }

    formatAddress(location: any): string {
      if (!location) return 'N/A';

      const address = location.addressLine1 || 'N/A';
      const city = location.city;
      
      // Check if address will wrap to 2 lines (approximately 25 chars per line for 108px width)
      const addressWillWrap = address.length > 25;
      
      if (addressWillWrap && city) {
        return `<div class="long-address">${address}<br><small style="font-size: 8px; color: #666;">${city}</small></div>`;
      }

      if (city) {
        return `${address}<br><small style="font-size: 8px; color: #666;">${city}</small>`;
      }

      return address;
    }

    getBannerNameFromStores(bannerId: string): string {
      const allStores = [...(this.wizard?.model?.storeList || []), ...((window as any).currentStores || []), ...((window as any).mapStores || [])];

      for (const store of allStores) {
        const storeObj = store.store || store;
        const bannerUuid = storeObj?.banner?.uuid;
        const bannerId2 = storeObj?.banner?.id;
        const bannerName = storeObj?.banner?.name;

        if (bannerUuid === bannerId || bannerId2 === bannerId) {
          return bannerName;
        }
      }

      // Try to get from global banner list if available
      const globalBanners = (window as any).bannerList || [];
      for (const banner of globalBanners) {
        if (banner.uuid === bannerId || banner.id === bannerId) {
          return banner.name;
        }
      }

      return bannerId; // Return original if not found
    }



    getStoresPerPage(): number {
      const footerPosition = 760;
      const tableTopPosition = 83;
      const tableHeaderHeight = 30;
      const normalRowHeight = 30;
      const longAddressRowHeight = 45;
      const safetyMargin = 40;

      const availableHeight = footerPosition - tableTopPosition - tableHeaderHeight - safetyMargin;

      // Count stores that fit based on their actual row heights using formatAddress logic
      let totalHeight = 0;
      let storeCount = 0;

      for (const store of this.filteredStores) {
        const addressText = store.address.replace(/<[^>]*>/g, '').trim();
        const addressWillWrap = addressText.length > 25;
        const hasCity = store.address.includes('<br>');
        const isLongAddress = addressWillWrap && hasCity;
        const rowHeight = isLongAddress ? longAddressRowHeight : normalRowHeight;

        if (totalHeight + rowHeight <= availableHeight) {
          totalHeight += rowHeight;
          storeCount++;
        } else {
          break;
        }
      }

      return Math.max(1, storeCount);
    }

    getTotalPages(): number {
      const activeFilterEntries = this.getActiveFilterEntries();
      const hasManyFilters = activeFilterEntries.length > 3;
      const hasBothFiltersAndAnalysis = this.showActiveFilters && this.showPolygonAnalysis;
      //const hasNeitherSection = !this.showActiveFilters && !this.showPolygonAnalysis;
      const tableStartPage = (hasBothFiltersAndAnalysis || hasManyFilters) ? 2 : 1;

      if (this.filteredStores.length === 0) {
        return tableStartPage;
      }

      let totalPages = tableStartPage;
      let remainingStores = [...this.filteredStores];

      if (tableStartPage === 1 && remainingStores.length > 0) {
        // Calculate page 1 stores with available space after other sections
        //const page1AvailableHeight = 190; // Conservative height for page 1
        //let page1Height = 0;
        let storesOnPage1 = 0;

        // Use same dynamic logic as getStoresForPage
        const baseStores = this.showActiveFilters ? 1 : 4;
        const maxStores = this.showPolygonAnalysis ? baseStores - 1 : baseStores;
        storesOnPage1 = Math.min(maxStores, remainingStores.length);

        remainingStores = remainingStores.slice(storesOnPage1);
        if (remainingStores.length === 0) {
          return totalPages;
        }
      }

      // Calculate remaining pages with fixed store count
      const storesPerAdditionalPage = 15; // Fixed number of stores per additional page
      
      while (remainingStores.length > 0) {
        const storesOnCurrentPage = Math.min(storesPerAdditionalPage, remainingStores.length);
        totalPages++;
        remainingStores = remainingStores.slice(storesOnCurrentPage);
      }

      return totalPages;
    }

    getStoresForPage(pageNum: number): any[] {
      const activeFilterEntries = this.getActiveFilterEntries();
      const hasManyFilters = activeFilterEntries.length > 3;
      const hasBothFiltersAndAnalysis = this.showActiveFilters && this.showPolygonAnalysis;
      const tableStartPage = (hasBothFiltersAndAnalysis || hasManyFilters) ? 2 : 1;

      if (pageNum === 1 && tableStartPage === 2) {
        return [];
      }

      // Calculate page breaks based on height
      const pageBreaks = [];
      let currentIndex = 0;

      if (tableStartPage === 1) {
        // Calculate actual stores on page 1 based on dynamic logic
        const baseStores = this.showActiveFilters ? 1 : 4;
        const maxStores = this.showPolygonAnalysis ? baseStores - 1 : baseStores;
        const storesOnPage1 = Math.min(maxStores, this.filteredStores.length);

        pageBreaks.push(storesOnPage1);
        currentIndex = storesOnPage1;
      }

      // Calculate remaining pages with fixed store count
      let remainingStores = this.filteredStores.slice(currentIndex);
      const storesPerAdditionalPage = 15; // Fixed number of stores per additional page

      while (remainingStores.length > 0) {
        const storesOnCurrentPage = Math.min(storesPerAdditionalPage, remainingStores.length);
        pageBreaks.push(currentIndex + storesOnCurrentPage);
        currentIndex += storesOnCurrentPage;
        remainingStores = remainingStores.slice(storesOnCurrentPage);
      }

      // Get stores for requested page
      const adjustedPageNum = tableStartPage === 1 ? pageNum - 1 : pageNum - tableStartPage;
      const startIndex = adjustedPageNum === 0 ? 0 : pageBreaks[adjustedPageNum - 1] || 0;
      const endIndex = pageBreaks[adjustedPageNum] || this.filteredStores.length;

      // Dynamic store count for page 1 based on sections present
      if (pageNum === 1 && tableStartPage === 1 && this.filteredStores.length > 0) {
        const baseStores = this.showActiveFilters ? 1 : 4;
        const maxStores = this.showPolygonAnalysis ? baseStores - 1 : baseStores;
        return this.filteredStores.slice(0, Math.min(maxStores, this.filteredStores.length));
      }

      return this.filteredStores.slice(startIndex, endIndex);
    }

    getAdditionalPages(): number[] {
      const totalPages = this.getTotalPages();
      const activeFilterEntries = this.getActiveFilterEntries();
      const hasManyFilters = activeFilterEntries.length > 3;
      const hasBothFiltersAndAnalysis = this.showActiveFilters && this.showPolygonAnalysis;
      const minPages = (hasBothFiltersAndAnalysis || hasManyFilters) ? 2 : 1;
      
      if (totalPages <= minPages) return [];
      
      const pages = [];
      for (let i = minPages + 1; i <= totalPages; i++) {
        const storesForPage = this.getStoresForPage(i);
        if (storesForPage && storesForPage.length > 0) {
          pages.push(i);
        }
      }
      return pages;
    }

    getAllLegendItems(): string[] {
      return [...this.storeLegendItems, ...this.storeLegendItemsSecondary];
    }

    getLegendSizeClass(): string {
      return 'legend-dynamic';
    }

    getLegendHeight(): number {
      const legendElement = document.querySelector('.store-legend-section');
      return legendElement ? legendElement.getBoundingClientRect().height : 40;
    }

    getActiveFiltersHeight(): number {
      const filtersElement = document.querySelector('.active-filters');
      return filtersElement ? filtersElement.getBoundingClientRect().height : 80;
    }

    getPolygonAnalysisHeight(): number {
      const polygonElement = document.querySelector('.polygon-analysis');
      return polygonElement ? polygonElement.getBoundingClientRect().height : 75;
    }

    getDynamicTopPositions(): any {
      const legendHeight = this.getLegendHeight();
      const baseMapBottom = 400; // Map section bottom
      const legendBottom = baseMapBottom + legendHeight + 10; // 10px gap
      
      const filtersHeight = this.getActiveFiltersHeight();
      const polygonHeight = this.getPolygonAnalysisHeight();
      
      let tablePosition;
      if (this.showActiveFilters && this.showPolygonAnalysis) {
        tablePosition = legendBottom + filtersHeight + polygonHeight + 10; // Minimal gap
      } else if (this.showActiveFilters) {
        tablePosition = legendBottom + filtersHeight + 5; // Small gap after filters
      } else if (this.showPolygonAnalysis) {
        tablePosition = legendBottom + polygonHeight + 5; // Small gap after polygon
      } else {
        tablePosition = legendBottom + 5; // Small gap after legend
      }
      
      return {
        activeFilters: legendBottom,
        polygonAnalysis: legendBottom + filtersHeight + 5, // Small gap after filters
        polygonAnalysisNoFilters: legendBottom,
        tableWithSections: tablePosition,
        tableNoSections: legendBottom + 5
      };
    }


    setMapSnapshot(mapImage: string): void {
      this.capturedMapImage = mapImage;
      console.log('Map snapshot set in PDF component');
    }

    async captureMapBeforeExport() {
      // Get map image from global state if available
      const globalMapImage = (window as any).capturedMapImage;
      if (globalMapImage) {
        this.capturedMapImage = globalMapImage;
        return;
      }
    }

//     private getAllPdfPages(): HTMLElement[] {
//   const allPdfPages = Array.from(document.querySelectorAll('[id^="map-search-pdf-page-"]')) as HTMLElement[];
//   return allPdfPages.sort((a, b) => {
//     const numA = parseInt(a.id.split('-').pop() || '0', 10);
//     const numB = parseInt(b.id.split('-').pop() || '0', 10);
//     return numA - numB;
//   });
// }


  private exportToPdfAndClose(): Observable<string> {
    return new Observable<string>(observer => {
      // Add small delay to ensure UI is stable before export
      setTimeout(() => {
        this.exportToPdf().then(() => {
          observer.next(null);
          observer.complete();
        }).catch(error => {
          console.error('PDF export failed:', error);
          this.isActionButtonEnabled = true; // Re-enable button on error
          observer.next(null);
          observer.complete();
        });
      }, 200);
    });
  }

  async exportToPdf(): Promise<void> {
    //const totalStart = performance.now();

    try {
      this.isExporting = true;

      // Capture map image first
      // const mapCaptureStart = performance.now();
      await this.captureCurrentMapState();
      

      // const pdfGenStart = performance.now();
      await this.generatePurePdf();
      
      
      this.toaster.info('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      this.toaster.error('Failed to export PDF: ' + error.message);
      throw error;
    } finally {
      this.isExporting = false;
    }

  }

  private async generatePurePdf(): Promise<void> {
    const jsPDF = (await import('jspdf')).default;
    const pdf = new jsPDF('p', 'mm', 'letter');
    
    // Set default font to Sofia Sans (automatically registered via import)
    pdf.setFont('SofiaSans-Regular', 'normal');
    
    // Track actual pages created
    let actualPageCount = 1;
    
    // Generate first page
    await this.generateFirstPage(pdf);
    
    // Generate additional pages if needed
    const totalPages = this.getTotalPages();
    const activeFilterEntries = this.getActiveFilterEntries();
    const hasManyFilters = activeFilterEntries.length > 3;
    const hasBothFiltersAndAnalysis = this.showActiveFilters && this.showPolygonAnalysis;
    const tableStartPage = (hasBothFiltersAndAnalysis || hasManyFilters) ? 2 : 1;
    
    for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
      // Always create page 2 if table starts on page 2, even with no stores
      if (pageNum === tableStartPage || this.getStoresForPage(pageNum).length > 0) {
        pdf.addPage();
        actualPageCount++;
        await this.generateAdditionalPage(pdf, pageNum);
      }
    }
    
    // Update footers with correct page count
    for (let i = 1; i <= actualPageCount; i++) {
      pdf.setPage(i);
      this.drawFooter(pdf, i, actualPageCount);
    }
    
    pdf.save('map-search-report.pdf');
  }

  private async generateFirstPage(pdf: any): Promise<void> {
    // Header
    await this.drawHeader(pdf);
    
    // User Description
    this.drawUserDescription(pdf);
    
    // Map Section
    await this.drawMapSection(pdf);
    
    // Store Legend
    let currentY = this.drawStoreLegend(pdf);
    
    // Active Filters (if applicable)
    if (this.showActiveFilters) {
      currentY = this.drawActiveFilters(pdf, currentY);
    }
    
    // Polygon Analysis (if applicable)
    if (this.showPolygonAnalysis) {
      currentY = this.drawPolygonAnalysis(pdf, currentY);
    }
    
    // Store Table (if fits on first page)
    const activeFilterEntries = this.getActiveFilterEntries();
    const hasManyFilters = activeFilterEntries.length > 3;
    const hasBothFiltersAndAnalysis = this.showActiveFilters && this.showPolygonAnalysis;
    const tableStartPage = (hasBothFiltersAndAnalysis || hasManyFilters) ? 2 : 1;
    
    if (tableStartPage === 1) {
      this.drawStoreTable(pdf, currentY, 1);
    }
  }

  private async generateAdditionalPage(pdf: any, pageNum: number): Promise<void> {
    // Table title
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Bold', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('FILTERED LOCATIONS (' + this.totalStoreCount + ' STORES)', 15, 25);
    
    // Add divider line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, 30, 200, 30);
    
    // Store table starting from top
    this.drawStoreTableForAdditionalPage(pdf, 38, pageNum);
  }

  private drawStoreTableForAdditionalPage(pdf: any, startY: number, pageNum: number): void {
    const stores = this.getStoresForPage(pageNum);
    
    let currentY = startY;
    
    // Set consistent line width for all borders
    pdf.setLineWidth(0.1);
    
    // Table header with borders
    pdf.setFillColor(233, 233, 233);
    pdf.rect(15, currentY, 185, 10, 'F');
    
    // Column borders
    pdf.setDrawColor(200, 200, 200);
    pdf.line(70, currentY, 70, currentY + 10);
    pdf.line(110, currentY, 110, currentY + 10);
    pdf.line(150, currentY, 150, currentY + 10);
    
    // Header border
    pdf.setDrawColor(180, 180, 180);
    pdf.rect(15, currentY, 185, 10, 'S');
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Bold', 'bold');
    pdf.text('Store Name', 20, currentY + 6);
    pdf.text('Banner', 75, currentY + 6);
    pdf.text('Format', 115, currentY + 6);
    pdf.text('Address', 155, currentY + 6);
    
    currentY += 10;
    
    if (stores.length === 0) {
      // No stores message
      pdf.setTextColor(0, 0, 0, 0.6);
      pdf.setFontSize(12);
      pdf.setFont('SofiaSans-Regular', 'normal');
      pdf.text('No stores available', 105, currentY + 15, { align: 'center' });
      return;
    }
    
    // Table rows
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.setTextColor(0, 0, 0, 0.85);
    
    for (let i = 0; i < stores.length; i++) {
      const store = stores[i];
      
      // Calculate dynamic row height based on address content
      const addressText = store.address.replace(/<[^>]*>/g, '').trim();
      const hasCity = store.address.includes('<br>');
      let addressLines = [];
      let cityLines = [];
      
      if (hasCity) {
        const parts = store.address.split('<br>');
        const mainAddress = parts[0].replace(/<[^>]*>/g, '').trim();
        const cityPart = parts[1] ? parts[1].replace(/<[^>]*>/g, '').trim() : '';
        addressLines = pdf.splitTextToSize(mainAddress, 45);
        cityLines = cityPart ? pdf.splitTextToSize(cityPart, 45) : [];
      } else {
        addressLines = pdf.splitTextToSize(addressText, 45);
      }
      
      const totalLines = addressLines.length + cityLines.length;
      const rowHeight = totalLines <= 1 ? 8 : Math.max(10, 6 + (totalLines * 3));
      
      // Check if row fits with footer spacing
      if (currentY + rowHeight > 240) break;
      
      if (i % 2 === 0) {
        pdf.setFillColor(255, 255, 255); // #FFF - white
      } else {
        pdf.setFillColor(245, 245, 245); // #F5F5F5 - gray
        pdf.rect(15, currentY, 185, rowHeight, 'F');
      }
      
      const storeNameLines = pdf.splitTextToSize(store.storeName, 50);
      const bannerLines = pdf.splitTextToSize(store.banner, 35);
      const format = pdf.splitTextToSize(store.format, 35)[0];
      
      // Center text vertically in cell for format
      const centerY = currentY + (rowHeight / 2) + 1;
      pdf.setFont('SofiaSans-Regular', 'normal');
      pdf.text(format, 115, centerY);
      
      // Handle store name with up to 2 lines
      if (storeNameLines.length === 1) {
        // Single line store name - center vertically
        pdf.text(storeNameLines[0], 20, centerY);
      } else {
        // Multi-line store name - display up to 2 lines
        const storeNameStartY = currentY + (rowHeight / 2) - 1;
        pdf.text(storeNameLines[0], 20, storeNameStartY);
        if (storeNameLines.length > 1) {
          pdf.text(storeNameLines[1], 20, storeNameStartY + 5);
        }
      }
      
      // Handle banner with up to 2 lines
      if (bannerLines.length === 1) {
        // Single line banner - center vertically
        pdf.text(bannerLines[0], 75, centerY);
      } else {
        // Multi-line banner - display up to 2 lines
        const bannerStartY = currentY + (rowHeight / 2) - 1;
        pdf.text(bannerLines[0], 75, bannerStartY);
        if (bannerLines.length > 1) {
          pdf.text(bannerLines[1], 75, bannerStartY + 5);
        }
      }
      
      // Keep address positioning unchanged
      const textY = currentY + 4.5;
      
      // Draw address with multiple lines
      pdf.setFontSize(9);
      let addressY = textY;
      for (let j = 0; j < addressLines.length; j++) {
        pdf.text(addressLines[j], 155, addressY);
        addressY += 4;
      }
      
      // Draw city lines
      if (cityLines.length > 0) {
        pdf.setFontSize(8);
        pdf.setTextColor(102, 102, 102); // #666
        for (let j = 0; j < cityLines.length; j++) {
          pdf.text(cityLines[j], 155, addressY);
          addressY += 4;
        }
        // Reset text color after city lines
        pdf.setTextColor(0, 0, 0, 0.85);
      }
      pdf.setFontSize(12);
      
      // Add column separators with consistent thin lines
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.1);
      pdf.line(70, currentY, 70, currentY + rowHeight);
      pdf.line(110, currentY, 110, currentY + rowHeight);
      pdf.line(150, currentY, 150, currentY + rowHeight);
      
      // Add thin row bottom border
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.1);
      pdf.line(15, currentY + rowHeight, 200, currentY + rowHeight);
      currentY += rowHeight;
    }
  }



  private async drawHeader(pdf: any): Promise<void> {
    // Header background
    pdf.setFillColor(15, 27, 77); // #0F1B4D
    pdf.rect(0, 0, 216, 28, 'F');
    
    // Add logo image
    try {
      const logoBase64 = await this.loadLogoAsBase64();
      if (logoBase64) {
        pdf.addImage(logoBase64, 'PNG', 12, 6, 50, 15);
      } else {
        throw new Error('Logo not loaded');
      }
    } catch (error) {
      console.error('Error loading logo:', error);
      // Fallback to text logo
      pdf.setFillColor(200, 200, 200);
      pdf.rect(15, 8, 35, 12, 'F');
      pdf.setTextColor(50, 50, 50);
      pdf.setFontSize(6);
      pdf.setFont('SofiaSans-Bold', 'normal');
      pdf.text('RETAILSTAT', 17, 16);
    }
    
    // Export info
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.text('Exported On', 185, 10);
    pdf.text(this.exportDate, 187, 16);
    pdf.text(this.exportTime, 185, 22);
  }

  private drawUserDescription(pdf: any): void {
    // Description box background
    pdf.setFillColor(239, 246, 255); // #EFF6FF
    pdf.rect(15, 32, 185, 25, 'F');
    
    // Blue left border
    pdf.setFillColor(37, 99, 235); // #2563EB
    pdf.rect(15, 32, 2, 25, 'F');
    
    // Title
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.text('User Description', 20, 42);
    
    // Description text
    pdf.setTextColor(55, 65, 81); // #374151
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Regular', 'normal');
    console.log("length of analysis description : - ",this.analysisDescription.length);
    const descriptionLines = pdf.splitTextToSize(this.analysisDescription, 175);
    pdf.text(descriptionLines, 20, 50);
  }

  private async drawMapSection(pdf: any): Promise<void> {
    const mapY = 65;
    
    if (this.capturedMapImage) {
      try {
        // Add image without specifying dimensions - jsPDF will use actual size
        const imgProps = pdf.getImageProperties(this.capturedMapImage);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        //const pdfHeight = pdf.internal.pageSize.getHeight();
        
        // Calculate scale to fit within reasonable bounds while maintaining aspect ratio
        const maxWidth = 150; // Max width in mm
        const maxHeight = 70;  // Fixed height in mm to prevent overlap
        
        let imgWidth = imgProps.width * 0.264583; // Convert px to mm
        let imgHeight = imgProps.height * 0.264583;
        
        // Scale down if too large
        if (imgWidth > maxWidth || imgHeight > maxHeight) {
          const scaleX = maxWidth / imgWidth;
          const scaleY = maxHeight / imgHeight;
          const scale = Math.min(scaleX, scaleY);
          imgWidth *= scale;
          imgHeight *= scale;
        }
        
        // Center the image
        const mapX = (pdfWidth - imgWidth) / 2;
        
        pdf.addImage(this.capturedMapImage, 'PNG', mapX, mapY, imgWidth, imgHeight);
      } catch (error) {
        console.error('Error adding map image:', error);
        const fallbackWidth = 150;
        const fallbackHeight = 70;
        const mapX = (216 - fallbackWidth) / 2;
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(mapX, mapY, fallbackWidth, fallbackHeight, 'S');
        pdf.setTextColor(100, 100, 100);
        pdf.setFontSize(12);
        pdf.setFont('SofiaSans-Regular', 'normal');
        pdf.text('Map image not available', mapX + fallbackWidth/2, mapY + fallbackHeight/2, { align: 'center' });
      }
    } else {
      const fallbackWidth = 150;
      const fallbackHeight = 70;
      const mapX = (216 - fallbackWidth) / 2;
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(mapX, mapY, fallbackWidth, fallbackHeight, 'S');
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(12);
      pdf.setFont('SofiaSans-Regular', 'normal');
      pdf.text('No map captured', mapX + fallbackWidth/2, mapY + fallbackHeight/2, { align: 'center' });
    }
  }

  private drawStoreLegend(pdf: any): number {
    if (this.totalStoreCount === 0) return 142;
    
    let currentY = 142;
    
    // Legend title
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('SofiaSans-Bold', 'bold');
    pdf.text('Store Legend', 15, currentY);
    
    // Legend items starting on same line as title
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.setTextColor(0, 0, 0, 0.7);
    
    const allLegendItems = this.getAllLegendItems();
    let x = 50; // Start after title
    let y = currentY; // Same line as title
    const maxWidth = 200;
    const itemSpacing = 10;
    
    for (const item of allLegendItems) {
      const itemWidth = pdf.getTextWidth(item);
      
      // Check if item fits on current line
      if (x + itemWidth > maxWidth && x > 85) {
        x = 15; // Next lines start from left margin
        y += 8;
      }
      
      pdf.text(item, x, y);
      x += itemWidth + itemSpacing;
    }
    
    return y + 12;
  }

  private drawActiveFilters(pdf: any, startY: number): number {
    const filterEntries = this.getActiveFilterEntries();
    if (filterEntries.length === 0) return startY;
    
    let currentY = startY;
    const maxWidth = 175; // Available width for content
    const labelStartX = 20;
    
    // Calculate total height needed for all filters with wrapping
    let totalContentHeight = 15; // Base padding
    filterEntries.forEach(filter => {
      const labelWidth = pdf.getTextWidth(filter.label + ': ');
      const valueWidth = maxWidth - labelWidth;
      const wrappedLines = pdf.splitTextToSize(filter.value, valueWidth);
      totalContentHeight += Math.max(6, wrappedLines.length * 6);
    });
    
    const boxHeight = Math.max(25, totalContentHeight);
    
    // Filter box background
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(0, 0, 0, 0.2);
    pdf.rect(15, currentY, 185, boxHeight, 'FD');
    
    currentY += 8;
    
    // Title
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('SofiaSans-Bold', 'bold');
    pdf.text('Active Filters', 20, currentY);
    
    currentY += 8;
    
    // Filter entries with wrapping
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Regular', 'normal');
    
    filterEntries.forEach(filter => {
      pdf.setTextColor(0, 0, 0);
      const labelText = filter.label + ': ';
      pdf.text(labelText, labelStartX, currentY);
      
      const labelWidth = pdf.getTextWidth(labelText);
      const valueStartX = labelStartX + labelWidth;
      const availableWidth = maxWidth - labelWidth;
      
      pdf.setTextColor(0, 0, 0, 0.7);
      const wrappedLines = pdf.splitTextToSize(filter.value, availableWidth);
      
      // Draw each line of the wrapped value
      wrappedLines.forEach((line, index) => {
        const lineY = currentY + (index * 6);
        const lineX = index === 0 ? valueStartX : labelStartX; // Subsequent lines start from left margin
        pdf.text(line, lineX, lineY);
      });
      
      currentY += Math.max(6, wrappedLines.length * 6);
    });
    
    return currentY + 14;
  }

  private drawPolygonAnalysis(pdf: any, startY: number): number {
    if (!this.showPolygonAnalysis) return startY;
    
    let currentY = startY;
    
    // Analysis box background
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(0, 0, 0, 0.2);
    pdf.rect(15, currentY, 185, 25, 'FD');
    
    currentY += 10;
    
    // Title
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Bold', 'normal');
    pdf.text('POLYGON ANALYSIS', 20, currentY);
    
    currentY += 8;
    
    // Metrics in three columns
    pdf.setFontSize(10);
    pdf.setFont('SofiaSans-Bold', 'normal');
    
    // Area column
    pdf.setTextColor(0, 0, 0);
    pdf.text('Area', 25, currentY);
    pdf.setTextColor(71, 121, 245); // #4779F5
    pdf.setFont('SofiaSans-Bold', 'normal');
    pdf.text(this.areaSquareFeet, 25, currentY + 6);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('SofiaSans-Bold', 'normal');
    pdf.text(this.areaAcres, 25, currentY + 12);
    
    // Perimeter column
    pdf.setTextColor(0, 0, 0);
    pdf.text('Perimeter', 85, currentY);
    pdf.setTextColor(71, 121, 245);
    pdf.setFont('SofiaSans-Bold', 'normal');
    pdf.text(this.perimeterFeet, 85, currentY + 6);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('SofiaSans-Bold', 'normal');
    pdf.text(this.perimeterMiles, 85, currentY + 12);
    
    // Center Point column
    pdf.setTextColor(0, 0, 0);
    pdf.text('Center Point', 145, currentY);
    pdf.setTextColor(0, 0, 0, 0.7);
    pdf.text(this.centerPointLat, 145, currentY + 6);
    pdf.text(this.centerPointLong, 145, currentY + 12);
    
    return currentY + 20;
  }

  private drawStoreTable(pdf: any, startY: number, pageNum: number): void {
    const stores = this.totalStoreCount === 0 ? [] : this.getStoresForPage(pageNum);
    
    let currentY = startY;
    const maxY = 250; // Adjusted page bottom limit for better space usage
    
    // Table title (only on first page if table starts there and has stores)
    if (pageNum === 1 && stores.length > 0) {
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('SofiaSans-Bold', 'bold');
      pdf.text('FILTERED LOCATIONS (' + this.totalStoreCount + ' STORES)', 15, currentY);
      currentY += 8;
      
      // Add divider line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(15, currentY, 200, currentY);
      currentY += 8;
    }
    
    // Always show table if it starts on page 1
    if (pageNum !== 1 && currentY + 20 > maxY) return;
    
    // Set consistent line width for all borders
    pdf.setLineWidth(0.1);
    
    // Table header with borders
    pdf.setFillColor(233, 233, 233); // #E9E9E9
    pdf.rect(15, currentY, 185, 10, 'F');
    
    // Column borders
    pdf.setDrawColor(200, 200, 200);
    pdf.line(70, currentY, 70, currentY + 10); // After Store Name
    pdf.line(110, currentY, 110, currentY + 10); // After Banner
    pdf.line(150, currentY, 150, currentY + 10); // After Format
    
    // Header border
    pdf.setDrawColor(180, 180, 180);
    pdf.rect(15, currentY, 185, 10, 'S');
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Bold', 'bold');
    pdf.text('Store Name', 20, currentY + 6);
    pdf.text('Banner', 75, currentY + 6);
    pdf.text('Format', 115, currentY + 6);
    pdf.text('Address', 155, currentY + 6);
    
    currentY += 10;
    
    if (stores.length === 0) {
      // No stores message
      pdf.setTextColor(0, 0, 0, 0.6);
      pdf.setFontSize(12);
      pdf.setFont('SofiaSans-Regular', 'normal');
      pdf.text('No stores available', 105, currentY + 15, { align: 'center' });
      return;
    }
    
    // Table rows
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.setTextColor(0, 0, 0, 0.85);
    
    for (let i = 0; i < stores.length; i++) {
      const store = stores[i];
      
      // Calculate dynamic row height based on address content
      const addressText = store.address.replace(/<[^>]*>/g, '').trim();
      const hasCity = store.address.includes('<br>');
      let addressLines = [];
      let cityLines = [];
      
      if (hasCity) {
        const parts = store.address.split('<br>');
        const mainAddress = parts[0].replace(/<[^>]*>/g, '').trim();
        const cityPart = parts[1] ? parts[1].replace(/<[^>]*>/g, '').trim() : '';
        addressLines = pdf.splitTextToSize(mainAddress, 45);
        cityLines = cityPart ? pdf.splitTextToSize(cityPart, 45) : [];
      } else {
        addressLines = pdf.splitTextToSize(addressText, 45);
      }
      
      const totalLines = addressLines.length + cityLines.length;
      const rowHeight = totalLines <= 1 ? 8 : Math.max(9, 6 + (totalLines * 3));
      
      // Check if row fits with footer spacing (skip check for page 1)
      if (pageNum !== 1 && currentY + rowHeight > maxY - 10) break;
      
      // Alternating row colors
      if (i % 2 === 0) {
        pdf.setFillColor(255, 255, 255); // #FFF - white
      } else {
        pdf.setFillColor(245, 245, 245); // #F5F5F5 - gray
        pdf.rect(15, currentY, 185, rowHeight, 'F');
      }
      
      // Store data
      const storeNameLines = pdf.splitTextToSize(store.storeName, 50);
      const bannerLines = pdf.splitTextToSize(store.banner, 35);
      const format = pdf.splitTextToSize(store.format, 35)[0];
      
      // Center text vertically in cell for format
      const centerY = currentY + (rowHeight / 2) + 1;
      pdf.setFont('SofiaSans-Regular', 'normal');
      pdf.text(format, 115, centerY);
      
      // Handle store name with up to 2 lines
      if (storeNameLines.length === 1) {
        // Single line store name - center vertically
        pdf.text(storeNameLines[0], 20, centerY);
      } else {
        // Multi-line store name - display up to 2 lines
        const storeNameStartY = currentY + (rowHeight / 2) - 1;
        pdf.text(storeNameLines[0], 20, storeNameStartY);
        if (storeNameLines.length > 1) {
          pdf.text(storeNameLines[1], 20, storeNameStartY + 5);
        }
      }
      
      // Handle banner with up to 2 lines
      if (bannerLines.length === 1) {
        // Single line banner - center vertically
        pdf.text(bannerLines[0], 75, centerY);
      } else {
        // Multi-line banner - display up to 2 lines
        const bannerStartY = currentY + (rowHeight / 2) - 1;
        pdf.text(bannerLines[0], 75, bannerStartY);
        if (bannerLines.length > 1) {
          pdf.text(bannerLines[1], 75, bannerStartY + 5);
        }
      }
      
      // Keep address positioning unchanged
      const textY = currentY + 4.5;
      
      // Draw address with multiple lines
      pdf.setFontSize(9);
      let addressY = textY;
      for (let j = 0; j < addressLines.length; j++) {
        pdf.text(addressLines[j], 155, addressY);
        addressY += 4;
      }
      
      // Draw city lines
      if (cityLines.length > 0) {
        pdf.setFontSize(8);
        pdf.setTextColor(102, 102, 102); // #666
        for (let j = 0; j < cityLines.length; j++) {
          pdf.text(cityLines[j], 155, addressY);
          addressY += 4;
        }
      }
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0, 0.85);
      
      // Add column separators with consistent thin lines
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.1);
      pdf.line(70, currentY, 70, currentY + rowHeight);
      pdf.line(110, currentY, 110, currentY + rowHeight);
      pdf.line(150, currentY, 150, currentY + rowHeight);
      
      // Add thin row bottom border
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.1);
      pdf.line(15, currentY + rowHeight, 200, currentY + rowHeight);
      
      currentY += rowHeight;
    }
  }



  private async loadLogoAsBase64(): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      };
      img.onerror = () => resolve(null);
      img.src = 'assets/images/rs_logo_blue_white_inline_transparent_cropped.png';
    });
  }

  private drawFooter(pdf: any, pageNum: number, actualTotalPages?: number): void {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerY = pageHeight - 15;
    
    // Footer line
    pdf.setDrawColor(0, 0, 0, 0.6);
    pdf.line(15, footerY - 5, 200, footerY - 5);
    
    // Footer text
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('SofiaSans-Regular', 'normal');
    pdf.text('retailstat.com', 15, footerY);
    const totalPages = actualTotalPages || this.getTotalPages();
    pdf.text('Page ' + pageNum + ' of ' + totalPages, 200, footerY, { align: 'right' });
  }




}
